import React, { useEffect, useState } from 'react';
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  TextField,
  FormHelperText,
} from '@material-ui/core';
import { get, isEmpty } from 'lodash';
import { Select } from '@material-ui/core';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import { injectIntl, IntlShape } from 'react-intl';

import { translate } from 'containers/services/translate';
import messages from 'containers/LawyerLegalProceedingsPage/messages';
import { JudgementOnMeritsType } from 'containers/LawyerLegalProceedingsPage/typings';
import { Listings } from 'containers/AppContainer/typings';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import SecondaryButton from 'components/Buttons/SecondaryButton';
import { BUTTON_COLORS } from 'constants/design';
import SuedPartyRecap from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/SuedPartyRecap';
import { ISuedPartyType } from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/typings';
import { getJudgementOnMeritsSuedPartiesList } from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/utils';

export interface CorrectionalJudgementProps {
  intl: IntlShape;
  listings: Listings;
  judgementOnMerits: JudgementOnMeritsType | {};
  handleSuedPartiesChange: Function;
}
const CorrectionalJudgement: React.FC<CorrectionalJudgementProps> = ({
  listings,
  judgementOnMerits,
  handleSuedPartiesChange,
  ...props
}): JSX.Element => {
  const [showForm, setShowForm] = useState<Boolean>(false);

  const [suedParties, setSuedParties] = useState<ISuedPartyType[]>([]);

  const [selectedSuedParty, setSelectedSuedParty] = useState<string>('');

  const [showAddForm, setShowAddForm] = useState<Boolean>(false);

  const [condamnationField, setCondamnationField] = useState<string>('');

  const [isInputChanged, setIsInputChanged] = useState<boolean>(true);
  const requiredFieldMessage = translate(messages.validateRequiredField, props);

  const handleChange = (e: React.ChangeEvent<{ value: string }>) => {
    setIsInputChanged(false);
    setCondamnationField(e.target.value);
  };

  const handleSelectChange = (
    event: React.ChangeEvent<{
      name?: string;
      value?: any;
    }>,
  ) => {
    setIsInputChanged(false);
    setSelectedSuedParty(event.target.value);
  };

  const validateForm = () => {
    if (selectedSuedParty === '' || condamnationField === '')
      setIsInputChanged(false);
    else {
      setSuedParties([
        ...suedParties,
        {
          condamnation: condamnationField,
          suedPartyId: selectedSuedParty,
        },
      ]);
      setShowAddForm(false);
    }
  };

  useEffect(() => {
    handleSuedPartiesChange(suedParties);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suedParties]);
  return (
    <div className="correctional-judgement">
      {!showForm ? (
        <PrimaryButton
          data-testid="add_sued_party"
          onClick={() => {
            setShowForm(true);
          }}
          lefticon={<AddCircleOutlineIcon className="icon" />}
          title={translate(messages.addSuedParty, props)}
          className="add-sued-party"
        />
      ) : (
        <Grid container>
          <Grid item xs={12}>
            {suedParties.map((el: ISuedPartyType, index: number) => (
              <SuedPartyRecap
                listingSuedParties={get(listings, 'suedParty', [])}
                index={index}
                suedParty={el}
                suedParties={suedParties}
                setSuedParties={setSuedParties}
                setShowForm={setShowForm}
              />
            ))}
          </Grid>
          <Grid item xs={12}>
            {showAddForm ? (
              <>
                <Grid container direction="row" spacing={2}>
                  <Grid item xs={5}>
                    <TextField
                      className="input-fields"
                      id="condamnation_field"
                      label={translate(messages.condamnationFieldTitle, props)}
                      value={condamnationField}
                      onChange={handleChange}
                      error={condamnationField === '' && !isInputChanged}
                      helperText={
                        condamnationField === '' && !isInputChanged
                          ? requiredFieldMessage
                          : ''
                      }
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <FormControl
                      error={selectedSuedParty === '' && !isInputChanged}
                    >
                      <InputLabel className="input-fields">
                        {translate(messages.suedPartySelect, props)}
                      </InputLabel>
                      <Select
                        className="input-fields"
                        onChange={handleSelectChange}
                      >
                        {!isEmpty(get(listings, 'suedParty', [])) &&
                          getJudgementOnMeritsSuedPartiesList(listings).map(
                            (suedParty: any) => (
                              <MenuItem value={suedParty.id} key={suedParty.id}>
                                {suedParty.title}
                              </MenuItem>
                            ),
                          )}
                      </Select>
                      {selectedSuedParty === '' && !isInputChanged && (
                        <FormHelperText className="errorSelect">
                          {requiredFieldMessage}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>

                <section className="submit-form">
                  <SecondaryButton
                    title={translate(messages.cancelButton, props)}
                    onClick={() => {
                      setShowAddForm(false);
                    }}
                  />
                  <PrimaryButton
                    disabled={false}
                    data-testid="save-sued-party"
                    background={BUTTON_COLORS.PRIMARY_BLUE}
                    border={BUTTON_COLORS.PRIMARY_BLUE}
                    titlecolor={BUTTON_COLORS.BLUE_BUTTON_TITLE}
                    title={translate(messages.saveButton, props)}
                    onClick={validateForm}
                  />
                </section>
              </>
            ) : (
              <PrimaryButton
                data-testid="add_new_sued_party"
                onClick={() => {
                  setShowAddForm(true);
                }}
                lefticon={<AddCircleOutlineIcon className="icon" />}
                title={translate(messages.addSuedParty, props)}
                className="add-sued-party"
              />
            )}
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default injectIntl(CorrectionalJudgement);
